import type { OnChanges, OnInit, SimpleChanges, TemplateRef } from '@angular/core';
import { ChangeDetectionStrategy, Component, ContentChild, EventEmitter, Input, Output } from '@angular/core';
import { InputBoolean } from '@common/helpers/convert';
import { EControlHandleEvents } from './sp-control-handle.enum';
import { BsDropdownDirective, BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { NgIf, NgTemplateOutlet, NgFor } from '@angular/common';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { TranslateModule } from '@ngx-translate/core';

type ReplaceEventPayload = 'fm' | 'stock';

@Component({
    selector: 'sp-control-handle',
    templateUrl: './sp-control-handle.component.html',
    styleUrls: ['./sp-control-handle.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgIf, NgTemplateOutlet, TooltipModule, BsDropdownModule, NgFor, TranslateModule],
})
export class SpControlHandleComponent implements OnInit, OnChanges {
    @ContentChild('variablesTemplate') public variablesTemplate: TemplateRef<any> | undefined;

    @Input() public selected: boolean = false;
    @Input() @InputBoolean() public controlHandleBtn!: boolean;
    @Input() @InputBoolean() public controlHandleCheckbox!: boolean;
    @Input() @InputBoolean() public controlHandleCheckboxChecked!: boolean;
    @Input() @InputBoolean() public controlHandleSubTextWarning!: boolean;
    @Input() @InputBoolean() public controlHandleSubTextDanger!: boolean;
    @Input() @InputBoolean() public controlHandleDisableDelete!: boolean;
    @Input() @InputBoolean() public controlHandleDisableCheckbox!: boolean;
    @Input() @InputBoolean() public galleryMode: boolean = false;
    @Input() public isDisableEditor: boolean = false;

    @Input() public controlHandleIcon!: string;
    @Input() public controlHandleImagePreviewSources: string[] | null | undefined = [];
    @Input() public controlHandleMainText!: string;
    @Input() public controlHandleSubText!: string;

    @Input() public controlHandleDisableDeleteTooltip!: string;

    @Input() public controlHandleDropdownActionList!: string[];

    @Output() public settingActionEvent: EventEmitter<void> = new EventEmitter<void>();
    @Output() public editActionEvent: EventEmitter<void> = new EventEmitter<void>();
    @Output() public replaceActionEvent: EventEmitter<ReplaceEventPayload> = new EventEmitter<ReplaceEventPayload>();
    @Output() public removeActionEvent: EventEmitter<void> = new EventEmitter<void>();
    @Output() public copyActionEvent: EventEmitter<void> = new EventEmitter<void>();
    @Output() public toggleCheckBoxEvent: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() public imageEditEvent: EventEmitter<void> = new EventEmitter<void>();
    @Output() public addSubMenuEvent: EventEmitter<void> = new EventEmitter<void>();
    @Output() public catalogActionEvent: EventEmitter<void> = new EventEmitter<void>();

    public eventsCount: number = 0;
    public controlHandleEvents = EControlHandleEvents;

    private imagePreviewIndex = 0;

    public ngOnChanges(changes: SimpleChanges): void {
        const previewSourcesChange = changes.controlHandleImagePreviewSources;

        if (
            previewSourcesChange &&
            !this.areImagePreviewSourcesEqual(previewSourcesChange.previousValue, previewSourcesChange.currentValue)
        ) {
            this.imagePreviewIndex = 0;
        }
    }

    public ngOnInit(): void {
        if (this.settingActionEvent.observed) {
            this.eventsCount += 1;
        }

        if (this.editActionEvent.observed) {
            this.eventsCount += 1;
        }

        if (this.replaceActionEvent.observed) {
            this.eventsCount += 1;
        }

        if (this.removeActionEvent.observed) {
            this.eventsCount += 1;
        }

        if (this.imageEditEvent.observed) {
            this.eventsCount += 1;
        }

        if (this.copyActionEvent.observed) {
            this.eventsCount += 1;
        }

        if (this.addSubMenuEvent.observed) {
            this.eventsCount += 1;
        }

        if (this.catalogActionEvent.observed) {
            this.eventsCount += 1;
        }
    }

    public removeAction(): void {
        if (this.controlHandleDisableDelete) {
            return;
        }

        if (this.removeActionEvent.observed) {
            this.removeActionEvent.emit();
        }
    }

    public get getDisabledTooltip(): string {
        return this.isDisableEditor ? 'disabled_image_editor_tooltip' : '';
    }

    public get hasImagePreview(): boolean {
        return this.imagePreviewSources.length > 0;
    }

    public get imagePreviewSrc(): string {
        return this.imagePreviewSources[this.imagePreviewIndex] || this.imagePreviewSources[0] || '';
    }

    public showNextImagePreview(): void {
        if (this.imagePreviewIndex < this.imagePreviewSources.length - 1) {
            this.imagePreviewIndex += 1;
        }
    }

    private get imagePreviewSources(): string[] {
        return this.controlHandleImagePreviewSources ?? [];
    }

    private areImagePreviewSourcesEqual(
        previousSources: string[] | null | undefined = [],
        currentSources: string[] | null | undefined = []
    ): boolean {
        const previous = previousSources ?? [];
        const current = currentSources ?? [];

        return previous.length === current.length && previous.every((source, index) => source === current[index]);
    }

    public isHasEventInDropdownActionList(eventName: string): boolean {
        if (this.controlHandleDropdownActionList?.length) {
            return Boolean(this.controlHandleDropdownActionList.find((item) => item === eventName));
        } else {
            return false;
        }
    }
}
